import { ReturnMonitoringDateType } from '@/services/return/returnMonitoring/types'

/**
 * 상태 탭 10종 — 1.0 `RETURN_MONITORING_STATUS`(functions/common/consts.js) 그대로 이관.
 * `key` 는 조회 API `ordStatus` 파라미터 값과 같다. '전체' 는 파라미터를 비운다(서버도 동치 처리).
 */
export const STATUS_TABS = [
  { key: '전체', label: '전체', message: '반품 상태 상세내용을 표시하는 곳입니다.' },
  { key: '반품취소', label: '반품취소', message: '고객님의 반품이 취소/철회되었습니다. 취소내역을 확인해주세요.' },
  { key: '접수완료', label: '접수완료', message: '고객님의 반품접수가 정상적으로 처리되었습니다.' },
  { key: '수거예정', label: '수거예정', message: '고객님의 반품이 택배사를 통해 안전하게 수거될 예정입니다.' },
  { key: '수거완료', label: '수거완료', message: '고객님의 반품이 택배사를 통해 안전하게 수거완료 되어, 곧 반품지로 출발예정입니다.' },
  { key: '반품배송중', label: '반품배송중', message: '고객님의 반품이 안전하게 배송중입니다. 곧 반품지로 배송완료될 예정입니다.' },
  {
    key: '센터도착',
    label: '센터도착',
    message: '고객님의 반품이 안전하게 반품지로 도착완료 되었으며, 정상/불량 판별을 위한 검수작업 대기 중입니다.',
  },
  { key: '검수완료', label: '검수완료', message: '고객님의 반품이 검수완료 되었습니다. 검수상태(정상/불량)를 확인해주세요.' },
  { key: '입고대기', label: '입고대기', message: '고객님의 반품이 검수완료되어, 적치대기 중이므로, 곧 적치 작업이 시작될 예정입니다.' },
  { key: '입고완료', label: '입고완료', message: '고객님의 반품이 최종적으로 적치완료 되었으며, 가용재고에 반영되었습니다.' },
] as const

export type StatusTabKey = (typeof STATUS_TABS)[number]['key']

export const ALL_STATUS_TAB: StatusTabKey = '전체'

/** 접수완료 상태 문자열 — 반품철회 사전 차단 기준(3.4). */
export const ACCEPTED_STATUS = '접수완료'

/**
 * 조회 기준일 옵션 4종 — 1.0 `RGR10_HEAD_SearchForm`(selectForm.py:9025~9041) 의 `SEARCH_STATUS`
 * 분기 그대로다. label 은 1.0 화면 표기, value 는 2.0 조회 API `dateType` 파라미터 값이다.
 */
export const DATE_TYPE_OPTIONS = [
  { value: 'RTN_DATE', label: '접수일(업로드)' },
  { value: 'ACT_RETURN_DATE', label: '도착일' },
  { value: 'INSPECT_DATE', label: '검수완료일' },
  { value: 'GR_ACT_DATE', label: '입고완료일' },
] as const

/** 조회 기준일 기본값 — 1.0 도 값이 없으면 접수일(업로드)로 본다. */
export const DEFAULT_DATE_TYPE: ReturnMonitoringDateType = 'RTN_DATE'

/** 실입고여부 옵션 — 1.0 COMBO 값 Y/N 고정. */
export const ACT_FLAG_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
] as const

/**
 * 기존 행 저장(E) 대상 편집 칸 6종 — 출고센터(GI_WH_NM)·반품수량(RTN_QTY)은 뺀다(D14, 저장 patch 필드에 없음).
 * 반품수량은 업로드한 새 행(I)에서만 편집 칸이고 그 값은 toSaveRow 의 신규 행 경로로 실린다(PD 확정 2026-10-01).
 */
export const EDITABLE_COLUMNS = ['orgInvoiceNo', 'rtnInvoiceNo', 'claimType', 'customerClaim', 'centerClaim', 'barcode'] as const

/** 저장 필드 최대 길이 — moms-be `ReturnMonitoringSaveRowRequest` `@field:Size` 그대로. */
export const SAVE_FIELD_MAX_LENGTHS: Record<string, number> = {
  orgInvoiceNo: 100,
  barcode: 60,
  rtnInvoiceNo: 100,
  claimType: 100,
  customerClaim: 2000,
  centerClaim: 2000,
  // receiptDate 는 그리드에서 편집하지 않지만 엑셀 업로드(신규 행)로 값이 들어오므로 함께 검사한다.
  receiptDate: 18,
}

/** 다운로드 상세사유 최소 길이 — BE `OutboundDownloadRequestDto.validateDetailedReason`. */
export const REASON_DETAIL_MIN_LENGTH = 5

export const MESSAGES = {
  searchFailed: '조회에 실패했습니다.',
  requiredFilter: '화주명과 조회기간을 선택해 주세요.',
  /**
   * LOGISPRD-12614 — 저장 대상이 비었을 때의 안내는 두 갈래다. 예전엔 한 문구가 둘을 겸했는데,
   * 편집은 해 두고 체크박스만 안 고른 사용자에게 "저장할 편집 내용이 없습니다" 라고 말해
   * 편집분이 날아간 것으로 읽혔다(LOGISPRD-12578 dev E2E 1회차).
   */
  saveNoEdits: '저장할 편집 내용이 없습니다.',
  saveNoSelection: '편집한 행을 선택해 주세요.',
  saveConfirm: (count: number) => `${count} 개의 행을 저장 하시겠습니까?`,
  saveFailed: '저장에 실패했습니다.',
  saveOwnershipDenied: '저장 권한이 없는 화주코드가 있어 저장하지 않았습니다. 화주코드를 확인해 주세요.',
  withdrawNotAccepted: '접수완료 상태만 취소할 수 있습니다.',
  /** 원송장번호가 비어 있는 행을 제외 목록에 적을 때 쓰는 자리 표시(1.0 은 빈 값을 그대로 찍었다). */
  unknownOrgInvoiceNo: '(원송장번호 없음)',
  /**
   * 접수완료가 아닌 행이 섞인 선택 — 1.0 `RGR10_DeleteForm`(deleteForm.py:941~962) 처럼 접수완료만
   * 처리하고 나머지는 원송장번호로 알린다. 보내기 전에 알려야 사용자가 취소할 수 있어 확인창에 함께 싣는다.
   */
  withdrawConfirmPartial: (targetCount: number, excludedOrgInvoiceNos: string[]) =>
    `접수완료 상태가 아닌 ${excludedOrgInvoiceNos.length}건을 제외하고 ${targetCount}건만 반품취소합니다.` +
    ` 제외된 원송장번호: ${excludedOrgInvoiceNos.join(', ')}. 진행하시겠습니까?`,
  /** 성공 토스트 — 데모 확인창의 기본 문구와 같다(옛 확인창 경로에서도 같은 문구를 낸다). */
  withdrawSuccess: '반품철회했습니다.',
  withdrawMultiShipper: '선택한 행에 둘 이상의 화주가 섞여 있습니다. 같은 화주끼리만 반품철회할 수 있습니다.',
  withdrawNoSelection: '반품철회할 행을 선택해 주세요.',
  withdrawFailed: '반품철회에 실패했습니다.',
  withdrawPartial: (requested: number, affected: number) =>
    `요청 ${requested}건 중 ${affected}건만 반품철회되었습니다. 조건(접수완료 상태·화주 일치)에 맞지 않는 행은 제외됐습니다.`,
  excelNoRows: '다운로드할 조회 결과가 없습니다.',
  excelDownloadFailed: '엑셀 다운로드에 실패했습니다.',
  excelDownloadRequiresSearch: '다운로드하려면 먼저 조회해 주세요.',
  passwordRuleHelp: '8자 이상, 대문자·소문자·숫자·특수문자를 모두 포함해야 합니다(공백 불가).',
  reasonDetailHelp: `${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해주세요.`,
  downloadReasonRequired: '사유코드를 선택해 주세요.',
  shipperLoadFailed: '화주 목록을 불러오지 못했습니다. 새로고침한 뒤 다시 시도해 주세요.',
  reasonLoadFailed: '다운로드 사유 목록을 불러오지 못했습니다. 새로고침한 뒤 다시 시도해 주세요.',
  excelUploadNoFile: '업로드할 엑셀 파일을 선택해 주세요.',
  excelUploadUnreadable: '엑셀 파일을 읽을 수 없습니다.',
  excelUploadEmpty: '가져올 데이터 행이 없습니다.',
  excelRecognized: (count: number) => `${count}행을 인식했습니다.`,
  excelUploaded: (count: number) => `${count}행을 가져왔습니다. 검토 후 저장하세요.`,
} as const
